"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

interface SpecialInfo {
  id: string;
  name: string;
  type: "SALE" | "OFFER";
  discount?: string;
  price?: string;
  // Parsed deal format for display (e.g., "2 for $75")
  dealFormat?: string;
}

interface SpecialsContextType {
  // Map of productId -> special info (for quick lookup)
  bundleProducts: Map<string, SpecialInfo>;
  saleProducts: Map<string, SpecialInfo>;
  isLoading: boolean;
  // Helper to check if a product has a bundle deal
  getBundleDeal: (productId: string) => SpecialInfo | null;
  // Helper to check if product is on sale
  getSaleDeal: (productId: string) => SpecialInfo | null;
}

const SpecialsContext = createContext<SpecialsContextType | null>(null);

// Shape of the /api/specials payload (POS specials cache).
interface ApiSpecial {
  id: string;
  name: string;
  type: "SALE" | "OFFER";
  discount?: string;
  products: { id: string }[];
}

export function SpecialsProvider({ children }: { children: ReactNode }) {
  const [bundleProducts, setBundleProducts] = useState<Map<string, SpecialInfo>>(new Map());
  const [saleProducts, setSaleProducts] = useState<Map<string, SpecialInfo>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchSpecialsData() {
      try {
        // Single call to our POS-backed specials cache — no more per-special
        // round-trips to a live API.
        const res = await fetch("/api/specials");
        const data = await res.json();
        const specials: ApiSpecial[] = data?.specials || [];

        const newBundleProducts = new Map<string, SpecialInfo>();
        const newSaleProducts = new Map<string, SpecialInfo>();

        for (const special of specials) {
          const info: SpecialInfo = {
            id: special.id,
            name: special.name,
            type: special.type,
            discount: special.discount,
            dealFormat: special.discount,
          };
          const target = special.type === "OFFER" ? newBundleProducts : newSaleProducts;
          for (const product of special.products || []) {
            // First deal wins per product, so a product isn't overwritten by a
            // later, weaker special.
            if (!target.has(product.id)) target.set(product.id, info);
          }
        }

        setBundleProducts(newBundleProducts);
        setSaleProducts(newSaleProducts);
      } catch (error) {
        console.error("Failed to fetch specials data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchSpecialsData();
  }, []);

  const getBundleDeal = (productId: string): SpecialInfo | null => {
    return bundleProducts.get(productId) || null;
  };

  const getSaleDeal = (productId: string): SpecialInfo | null => {
    return saleProducts.get(productId) || null;
  };

  return (
    <SpecialsContext.Provider value={{
      bundleProducts,
      saleProducts,
      isLoading,
      getBundleDeal,
      getSaleDeal
    }}>
      {children}
    </SpecialsContext.Provider>
  );
}

export function useSpecials() {
  const context = useContext(SpecialsContext);
  if (!context) {
    // Return a default value if not wrapped in provider (graceful fallback)
    return {
      bundleProducts: new Map(),
      saleProducts: new Map(),
      isLoading: false,
      getBundleDeal: () => null,
      getSaleDeal: () => null,
    };
  }
  return context;
}
